// Copyright (c) 2018 Comsearch, Inc., All rights reserved

import React from 'react'
import { bool, func, object, string } from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { reInitializeForm } from '../helpers'
import { SelectDynamic } from 'components/Form'

import {
  verifyParentFormWasInitializedProperly,
} from './helpers'

import {
  Field,
  hasSubmitSucceeded,
  isPristine,
  submit,
} from 'redux-form'

// The pagination component should only be visible when the form is pristine and submitSucceeded === true
// When user changes the page, although they'll dirty the form, the submit handler
// on <SearchAndSelect /> knows to reinitialize the form as long as it was successful.

// NOTE - this requirement is also reflected in ./AdvancedSearchWrapper
// where the form, if pristine, will toggle the wrapper then re-initialize
// so that this component is visible despite a form value (advToggle) changing.

// NOTE - this component assumes that the form's values has a pagination object with this scheme
// {
//   "count": INT,
//   "totalPages": INT,
//   "page": INT,
// }

const decorationStyles = {
  borderRight: '1px solid',
}

class Pagination extends React.Component {
  static propTypes = {
    idRoot: string.isRequired,
    pagination: object,
    reInitializeForm: func.isRequired,
    showPagination: bool.isRequired,
    sourceForm: string.isRequired,
    submit: func.isRequired,
    verifyParentFormWasInitializedProperly: func.isRequired,
  }

  state = {
    count: null,
    totalPages: null,
    options: [],
  }

  // when a form has persisting pagination values,
  // assign necessary values to component state
  async componentDidMount () {
    const {
      pagination,
      sourceForm,
      verifyParentFormWasInitializedProperly,
    } = this.props

    // ensures that pagination was set up properly.
    // NOTE - this should only display during development and never in prod.
    verifyParentFormWasInitializedProperly(sourceForm)

    if (pagination) {
      await this.setPaginationValues(pagination)
    }
  }

  // When pagination info updates,
  // We need to update the total number of pages shown
  // So we only present the user with options which actually reflect
  // the total number of pages matched against the query.
  componentWillReceiveProps ({ pagination }) {
    const {
      count,
      totalPages,
    } = this.state

    if (pagination && (pagination.count !== count || pagination.totalPages !== totalPages)) {
      this.setPaginationValues(pagination)
    }
  }

  // ignore blur on this field so we don't have to reinitialize the form if we are blurred.
  onBlurCustom = () => false

  onChangeCustom = async (e, c) => {
    const {
      reInitializeForm,
      sourceForm,
      submit,
    } = this.props

    await c(e)
    // initialize here so the pagination component does not hide
    // while we're waiting for the updated search/page results.
    await reInitializeForm(sourceForm)
    submit(sourceForm)
  }

  setPaginationValues = ({ count, totalPages }) => {
    // these options will be used in the <select /> element
    // to display the page options which are available based
    // upon the given set of results from <SearchAndSelect />
    const options = Array(totalPages).fill().map((d, i) => {
      const ii = i + 1
      return ({
        label: ii,
        value: i,
      })
    })

    this.setState(() => ({
      count,
      totalPages,
      options,
    }))
  }

  render () {
    const {
      onBlurCustom,
      onChangeCustom,
      props,
      state,
    } = this

    const {
      idRoot,
      showPagination,
    } = props

    const {
      count,
      options,
      totalPages,
    } = state

    if (!showPagination || !totalPages) {
      return null
    }

    return (
      <h5 className={'padding-half padding-bottom-0 display-flex justify-content-end align-items-center'}>
        <span
          className={'padding-right-half'}
          id={`${idRoot}pagination-count`}
          style={decorationStyles}>
          {`${count} result${count > 1 ? 's' : ''}`}
        </span>
        <span
          className={'padding-half'}>
          {' Page '}
        </span>
        <Field
          component={SelectDynamic}
          id={`${idRoot}pagination`}
          name={'pagination.page'}
          onBlurCustom={onBlurCustom}
          onChangeCustom={onChangeCustom}
          options={options}
          selectLabel={''} />
        <span
          className={'padding-half'}
          id={`${idRoot}pagination-page-count`}>
          {` of ${totalPages}`}
        </span>
      </h5>
    )
  }
}

const mapStateToProps = ({ form }, { sourceForm }) => {
  const pagination = (
    form[sourceForm] && form[sourceForm].values
      ? form[sourceForm].values.pagination
      : null
  )

  // Only display the pagination if the form is pristine AND
  // it has been successfully submitted already.
  // NOTE - this component does not call submittedFormIsPristine because
  // that thunk is used specifically to work around react's natural life cycle handling.
  // since we're within a component, we actually want to allow for react's natural life cycle rendering.
  const showPagination = isPristine(sourceForm)({ form }) && hasSubmitSucceeded(sourceForm)({ form })
  return {
    pagination,
    showPagination,
  }
}

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    reInitializeForm,
    submit,
    verifyParentFormWasInitializedProperly,
  }, dispatch)
)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Pagination)
